import { Mark } from '@tiptap/core';
import { registerCustomProtocol, reset } from 'linkifyjs';
import { Plugin } from 'prosemirror-state';
import { autolink } from './helpers/autolink';
import { pasteHandler } from './helpers/pasteHandler';

import { StoreService } from '@utils/store.service';

export interface LinkProtocolOptions {
    scheme: string;
    optionalSlashes?: boolean;
}

export interface LinkOptions {
    /**
     * If enabled, it adds links as you type.
     */
    autolink: boolean,
    /**
     * An array of custom protocols to be registered with linkifyjs.
     */
    protocols:  Array<LinkProtocolOptions | string>,
    /**
     * If enabled, links will be opened on click.
     */
    openOnClick: boolean,
    /**
     * Adds a link to the current selection if the pasted content only contains an url.
     */
    linkOnPaste: boolean,
    /**
     * A validation function that modifies link verification for the auto linker.
     * @param url - The url to be validated.
     * @returns - True if the url is valid, false otherwise.
     */
    validate?: (url: string) => boolean,
    rightClickHandler?: (url: string, event: MouseEvent) => void,
    _store?: StoreService
}

export const Link = Mark.create<LinkOptions>({
    name: 'link',
    priority: 1000,
    keepOnSplit: false,
    excludes: 'italic strike underline subscript superscript',
    exitable: true,
    spanning: false,
    inclusive: true,

    onCreate() {
        this.options.protocols.forEach(protocol => {
            if (typeof protocol === 'string') {
                registerCustomProtocol(protocol);
                return;
            }
            registerCustomProtocol(protocol.scheme, protocol.optionalSlashes)
        })
    },

    onUpdate() {
        // const state = this.editor.view.state;
        // const pos = state.selection.$head;
        // const tn = pos.doc.nodeAt(pos.pos - pos.textOffset);
        // const anchor = state.selection.$anchor;
        // const linkMarkInfo = markPosition(state, anchor.pos, state.schema.marks.link);
        // if (tn && linkMarkInfo) {
        //     const { from, to, mark } = linkMarkInfo;
        //     state.tr.removeMark(from, to, mark);
        //     (mark.attrs as any) = { ...mark.attrs, href: tn.text };
        //     state.tr.addMark(from, to, mark);
        //     this.editor.view.dispatch(state.tr);
        // }
    },

    onDestroy() {
        reset();
    },

    // inclusive() {
    //     return this.options.autolink
    // },

    addOptions() {
        return {
            openOnClick: true,
            linkOnPaste: true,
            autolink: true,
            protocols: [],
            validate: undefined,
        };
    },

    addAttributes() {
        return {
            href: {
                default: null,
            },
        };
    },

    parseHTML() {
        return [
            { tag: 'a[href]' },
        ];
    },

    renderHTML({ mark }) {
        const a = document.createElement('a');
        a.href = mark.attrs.href;
        a.setAttribute('target', '_blank');
        a.setAttribute('rel', 'noopener noreferrer nofollow');
        a.style.marginLeft = '4px';
        a.style.marginRight = '4px';
        if (!this.options.openOnClick) {
            a.style.cursor = 'text';
        }
        a.oncontextmenu = (ev) => this.options.rightClickHandler ? this.options.rightClickHandler(mark.attrs.href, ev) : null;
        return a;
    },

    addCommands() {
        return {
            setLink: attributes => ({ chain }) => {
                return chain()
                    .setMark(this.name, attributes)
                    .setMeta('preventAutolink', true)
                    .run();
            },

            toggleLink: attributes => ({ chain }) => {
                return chain()
                    .toggleMark(this.name, attributes, { extendEmptyMarkRange: true })
                    .setMeta('preventAutolink', true)
                    .run();
            },

            unsetLink: () => ({ chain }) => {
                return chain()
                    .unsetMark(this.name, { extendEmptyMarkRange: true })
                    .setMeta('preventAutolink', true)
                    .run();
            },
        };
    },

    addProseMirrorPlugins() {
        const plugins: Plugin[] = [];

        if (this.options.autolink) {
            plugins.push(autolink({
                type: this.type,
                validate: this.options.validate,
            }));
        }

        if (this.options.linkOnPaste) {
            plugins.push(pasteHandler({
                editor: this.editor,
                type: this.type,
                _store: this.options._store
            }));
        }

        return plugins;
    },
});

// export function markPosition(state: EditorState, pos: number, markType: MarkType) {
//     const $pos = state.doc.resolve(pos);

//     const { parent, parentOffset } = $pos;
//     const start = parent.childAfter(parentOffset);
//     if (!start.node) return;

//     const mark = start.node.marks.find((mark) => mark.type === markType);
//     if (!mark) return;

//     let startIndex = $pos.index();
//     let from = $pos.start() + start.offset;
//     let endIndex = startIndex + 1;
//     let to = from + start.node.nodeSize;
//     while (startIndex > 0 && mark.isInSet(parent.child(startIndex - 1).marks)) {
//         startIndex -= 1;
//         from -= parent.child(startIndex).nodeSize;
//     }
//     while (endIndex < parent.childCount && mark.isInSet(parent.child(endIndex).marks)) {
//         to += parent.child(endIndex).nodeSize;
//         endIndex += 1;
//     }
//     return { from, to, mark };
// }
